// ═══════════════════════════════════════════════════════════════════════════
// MODULE MANAGEMENT TYPES
// Types for system modules, plan modules, and tenant module access
// ═══════════════════════════════════════════════════════════════════════════

export type ModuleCategory = "CORE" | "ADVANCED" | "COMPLIANCE" | "PREMIUM";
export type PageOverrideMode = "HIDDEN" | "DENIED";

export interface PagePolicy {
  key: string;
  path: string;
  match: "exact" | "prefix";
  section: string;
  label: string;
  icon: string;
  order: number;
  navigation: boolean;
  release: "ENABLED" | "DISABLED";
  requiredModule?: string;
  requiredPermission?: string;
  requiredRole?: string;
}

export interface TenantPageOverride {
  id: string;
  tenantId: string;
  pageKey: string;
  mode: PageOverrideMode;
  reason: string;
  addedBy: string;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  effective: boolean;
}

export interface TenantPageOverrideList {
  accessVersion: number;
  overrides: TenantPageOverride[];
}

/**
 * System Module - represents a feature/module available in the platform
 */
export interface SystemModule {
  id: string;
  code: string;
  name: string;
  description: string | null;
  category: ModuleCategory;
  isCore: boolean;
  isAddOn: boolean;
  addOnPriceMonthly: string | null;
  addOnPriceYearly: string | null;
  sortOrder: number;
  isActive: boolean;
  /** Lucide icon name for sidebar display */
  icon: string | null;
  /** Routes protected by this module */
  routes: string[];
  /** Sidebar configuration for this module */
  sidebarConfig: SidebarConfig | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Sidebar item configuration for a module
 */
export interface SidebarItem {
  name: string;
  href: string;
  icon?: string;
  requiredRole?: string;
  requiredModule?: string;
}

/**
 * Sidebar configuration for a module
 */
export interface SidebarConfig {
  title?: string;
  items: SidebarItem[];
}

export interface CreateSystemModulePayload {
  code: string;
  name: string;
  category: ModuleCategory | string;
  description?: string;
  icon?: string;
  sortOrder?: number;
  isActive?: boolean;
  routes?: string[];
  sidebarConfig?: SidebarConfig;
}

/**
 * Plan Module - represents a module assigned to a plan
 */
export interface PlanModule {
  id: string;
  planId: string;
  moduleId: string;
  module: SystemModule;
}

/**
 * Tenant Module Override - represents a module override for a tenant
 */
export interface TenantModuleOverride {
  id: string;
  tenantId: string;
  moduleId: string;
  isEnabled: boolean;
  expiresAt: string | null;
  reason: string | null;
  enabledBy: string | null;
  module: SystemModule;
}

/**
 * Module access status for a tenant
 */
export interface TenantModuleAccess {
  code: string;
  name: string;
  category: ModuleCategory;
  isEnabled: boolean;
  source: "plan" | "PLAN" | "OVERRIDE" | "ADDON" | "TRIAL";
  expiresAt?: string;
}

/**
 * Sidebar group compiled from enabled modules for a tenant
 */
export interface SidebarGroup {
  moduleCode: string;
  title: string;
  items: SidebarItem[];
  sortOrder: number;
}

/**
 * Response from getTenantModules endpoint
 */
export interface TenantModuleList {
  enabled: string[];
  disabled: string[];
  accessVersion: number;
  pageOverrides: Partial<Record<string, PageOverrideMode>>;
  all: TenantModuleAccess[];
  sidebarConfig?: { groups: SidebarGroup[] };
}

/**
 * Module access check result
 */
export interface ModuleAccessResult {
  hasAccess: boolean;
  module: string;
  source: "plan" | "tenant_override" | "store_override" | "addon";
  expiresAt?: string;
  reason?: string;
}

/**
 * Payload for enabling a module for a tenant
 */
export interface EnableModulePayload {
  moduleCode: string;
  reason: string;
  expiresAt?: string;
}

/**
 * Payload for disabling a module for a tenant
 */
export interface DisableModulePayload {
  moduleCode: string;
  reason: string;
}

export interface BulkAssignModulesPayload {
  tenantIds: string[];
  moduleCodes: string[];
  action: "enable" | "disable";
  reason: string;
  expiresAt?: string;
}

/**
 * Extended Plan with modules
 */
export interface PlanWithModules {
  id: string;
  name: string;
  type: string;
  description: string | null;
  priceMonthly: string | null;
  priceYearly: string | null;
  maxStores: number;
  maxUsers: number;
  storageGB: number;
  features: string[];
  trialDays: number;
  isActive: boolean;
  planModules: PlanModule[];
}

/**
 * Payload for creating/updating a plan with modules
 */
export interface PlanModulePayload {
  planId: string;
  moduleCodes: string[];
}

/**
 * Module category metadata for UI display
 */
export const MODULE_CATEGORIES: Record<
  ModuleCategory,
  { label: string; color: string; description: string }
> = {
  CORE: {
    label: "Core",
    color: "bg-green-100 text-green-700",
    description: "Essential business features (POS, Inventory)",
  },
  ADVANCED: {
    label: "Advanced",
    color: "bg-purple-100 text-purple-700",
    description: "Growth features (Analytics, Reports, Multi-store)",
  },
  COMPLIANCE: {
    label: "Compliance",
    color: "bg-yellow-100 text-yellow-700",
    description: "Regulatory features (GST, FSSAI, Drug Schedule)",
  },
  PREMIUM: {
    label: "Premium",
    color: "bg-red-100 text-red-700",
    description: "Enterprise-grade features (API Access, White-label)",
  },
};
